import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Download } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface DocumentCardProps {
  title: string;
  type: string;
  downloadUrl: string;
  description: string;
  createdAt?: string;
}

const DocumentCard = ({ title, type, downloadUrl, description, createdAt }: DocumentCardProps) => {
  const { toast } = useToast();

  // Check if new (within last 7 days)
  const isNew = createdAt ? 
    (new Date().getTime() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24) <= 7 : false;

  const getTypeColor = () => {
    const lowerType = type?.toLowerCase();
    if (lowerType === 'excel') return 'bg-hub-teal';
    if (lowerType === 'powerpoint' || lowerType === 'ppt') return 'bg-muted-foreground';
    if (lowerType === 'figma') return 'bg-hub-purple';
    if (lowerType === 'word' || lowerType === 'doc') return 'bg-primary';
    return 'bg-hub-purple';
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const { data, error } = await supabase.storage
        .from('templates')
        .download(downloadUrl);

      if (error || !data) {
        toast({
          title: "Download Failed",
          description: "Could not download the template. Please try again.",
          variant: "destructive",
        });
        return;
      }

      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadUrl.split('/').pop() || 'template';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({ title: "Download started" });
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="group overflow-hidden border-border hover:shadow-md transition-all duration-200">
      {/* FTS Hub Branded Area */}
      <div className={`aspect-[4/3] flex items-center justify-center ${getTypeColor()}`}>
        <div className="text-center text-white">
          <span className="font-elegant text-3xl md:text-4xl font-semibold tracking-wide">
            FTS
          </span>
          <span className="block font-elegant text-lg md:text-xl font-normal opacity-90">
            Hub
          </span>
        </div>
      </div>
      
      <CardContent className="p-4">
        {/* Badges */}
        <div className="flex items-center gap-2 mb-2">
          {isNew && (
            <Badge className="bg-primary text-primary-foreground text-xs">
              New
            </Badge>
          )}
          <Badge variant="outline" className="text-xs">
            {type}
          </Badge>
        </div>

        {/* Title */}
        <h3 className="font-semibold text-foreground mb-1 line-clamp-2 group-hover:text-primary transition-colors">
          {title}
        </h3>
        
        {/* Description */}
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {description || "Professional template for your projects."}
        </p>

        {/* Download Button */}
        <Button
          onClick={handleDownload}
          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
          size="sm"
        >
          <Download className="h-4 w-4 mr-2" />
          Download
        </Button>
      </CardContent>
    </Card>
  );
};

export default DocumentCard;
